// M004-F0646 / F0647 — pure model helpers for the users-drawer "Price plans"
// section. No React, no network, and NO RATE ARITHMETIC: every amount that
// reaches the screen is a Core-computed `*_minor` integer that is only
// FORMATTED here (design M004-F0646-F0649 §4 "Consumer rules").

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const DAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

// Design §3: a fixed 6-entry palette PLUS a per-plan pattern so colour is
// never the only signal. `BASE_STYLE` is the locked room-default card.
export const PLAN_PALETTE = [
  { color: '#2e7d32', pattern: 'diagonal' },
  { color: '#7b1fa2', pattern: 'dots' },
  { color: '#ef6c00', pattern: 'cross' },
  { color: '#00838f', pattern: 'vertical' },
  { color: '#c2185b', pattern: 'horizontal' },
  { color: '#1565c0', pattern: 'solid' },
];
export const BASE_STYLE = { color: '#78909c', pattern: 'solid' };

export const PATTERN_CSS = {
  solid: 'none',
  diagonal: 'repeating-linear-gradient(45deg, rgba(255,255,255,.55) 0 2px, transparent 2px 5px)',
  dots: 'radial-gradient(rgba(255,255,255,.7) 1px, transparent 1.5px) 0 0 / 4px 4px',
  cross: 'repeating-linear-gradient(0deg, rgba(255,255,255,.55) 0 1px, transparent 1px 4px)',
  vertical: 'repeating-linear-gradient(90deg, rgba(255,255,255,.55) 0 1px, transparent 1px 4px)',
  horizontal: 'repeating-linear-gradient(135deg, rgba(255,255,255,.55) 0 2px, transparent 2px 5px)',
};

/**
 * Stable plan -> {color, pattern}. The base plan is always grey/solid; other
 * ids are assigned in ascending id order so the same set of plans always gets
 * the same styles (distinct for up to six plans).
 */
export const buildPlanStyles = (planIds, basePlanId = null) => {
  const styles = new Map();
  if (basePlanId !== null && basePlanId !== undefined) styles.set(String(basePlanId), BASE_STYLE);
  const others = [...new Set((planIds || []).map(String))]
    .filter((id) => id !== String(basePlanId))
    .sort((a, b) => Number(a) - Number(b));
  others.forEach((id, index) => styles.set(id, PLAN_PALETTE[index % PLAN_PALETTE.length]));
  return styles;
};

export const styleForPlan = (styles, planId) => styles.get(String(planId)) || BASE_STYLE;

// ---- formatting (display only) -------------------------------------------

export const formatMinor = (minor) => {
  if (!Number.isFinite(minor)) return '';
  const whole = Math.trunc(minor / 100);
  const pence = Math.abs(minor % 100);
  return pence === 0 ? `£${whole}` : `£${whole}.${String(pence).padStart(2, '0')}`;
};
export const formatMinorExact = (minor) => (
  Number.isFinite(minor) ? `£${(minor / 100).toFixed(2)}` : ''
);
export const formatPerHour = (minor) => (Number.isFinite(minor) ? `${formatMinor(minor)}/h` : '');

// ---- weekday masks / zones -----------------------------------------------

/** M005-D004 §12: bit (1 << (iso_weekday - 1)); Mon = 1 ... Sun = 64. */
export const daysFromMask = (mask) => DAY_LABELS.map((_, index) => (Number(mask) & (1 << index)) !== 0);

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/** Only zones with `applies_only_within` gate a plan (Z3). */
export const gatingZones = (plan) => (
  Array.isArray(plan?.zones) ? plan.zones.filter((zone) => zone?.applies_only_within === true) : []
);

/**
 * Scope chips for a plan from the SERVER summary (A7 `plan.zones`):
 * days (union of gating zones' masks, else all seven) and time text.
 */
export const scopeSummary = (plan) => {
  const zones = gatingZones(plan);
  if (zones.length === 0) {
    return { zoned: false, days: DAY_LABELS.map(() => true), times: ['Any time'] };
  }
  const days = DAY_LABELS.map((_, index) => zones.some((zone) => (Number(zone.weekday_mask) & (1 << index)) !== 0));
  const times = [...new Set(zones.map((zone) => `${zone.start_local}–${zone.end_local}`))];
  return { zoned: true, days, times };
};

/** Mini week glyph cells: 7 days x 8 two-hour bands (08:00-24:00) "on" flags. */
export const glyphCells = (plan) => {
  const zones = gatingZones(plan);
  const rows = [];
  for (let row = 0; row < 8; row += 1) {
    const from = 8 * 60 + row * 120;
    const to = from + 120;
    rows.push(DAY_LABELS.map((_, day) => {
      if (zones.length === 0) return true;
      return zones.some((zone) => (
        (Number(zone.weekday_mask) & (1 << day)) !== 0
        && to > minutesOf(zone.start_local)
        && from < minutesOf(zone.end_local)
      ));
    }));
  }
  return rows;
};

// ---- dates ---------------------------------------------------------------

export const localIsoDate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const addDaysIso = (iso, days) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return localIsoDate(date);
};

export const daysBetweenIso = (fromIso, toIso) => {
  const [fy, fm, fd] = String(fromIso).split('-').map(Number);
  const [ty, tm, td] = String(toIso).split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatShortDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!match) return '';
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]}`;
};

// ---- grouping (design §3.2, rule D4) --------------------------------------

/**
 * A live card = an assignment that has not ended before `today`.
 * (Ended rows are history; the Prior-plans expander of M004-F0615 covers them.)
 */
export const isLiveAssignment = (assignment, today) => (
  !assignment.effective_to || String(assignment.effective_to) >= today
);

/**
 * D4: assignments whose plans share `template_plan_id` AND the same effective
 * dates form ONE card with room chips. A plan with no template stands alone.
 */
export const groupAssignments = (assignments, today = localIsoDate()) => {
  const groups = new Map();
  (assignments || []).filter((a) => isLiveAssignment(a, today)).forEach((assignment) => {
    const plan = assignment.plan || {};
    const template = plan.template_plan_id ?? null;
    const key = [
      template !== null ? `t${template}` : `p${assignment.pricing_plan_id}`,
      assignment.effective_from || '',
      assignment.effective_to || '',
    ].join('|');
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: assignment.plan_name || plan.name || `Plan ${assignment.pricing_plan_id}`,
        templatePlanId: template,
        effectiveFrom: assignment.effective_from || null,
        effectiveTo: assignment.effective_to || null,
        plan,
        items: [],
      });
    }
    groups.get(key).items.push(assignment);
  });
  return [...groups.values()].map((group) => ({
    ...group,
    rooms: group.items.map((item) => ({
      assignmentId: item.id,
      roomId: item.plan?.room?.id ?? item.room_id ?? null,
      roomName: item.plan?.room?.name || (item.room_id ? `Room ${item.room_id}` : 'Room'),
    })),
    redundant: group.items.every((item) => item.redundant === true),
    rateSummary: group.plan?.rate_summary || null,
    planIds: group.items.map((item) => item.pricing_plan_id),
  })).sort((a, b) => (
    String(a.effectiveFrom).localeCompare(String(b.effectiveFrom)) || a.name.localeCompare(b.name)
  ));
};

// ---- picker grouping (plans of one template across rooms) ------------------

/**
 * `GET /plans` rows -> picker rows. Room plans sharing a `template_plan_id`
 * are ONE row with several rooms ("Add for all or selected rooms").
 */
export const buildPickerPlans = (plans, roomNames = {}) => {
  const eligible = (plans || []).filter((plan) => (
    !plan.is_template && plan.status !== 'retired' && !plan.is_public_default
  ));
  const groups = new Map();
  eligible.forEach((plan) => {
    const key = plan.template_plan_id !== null && plan.template_plan_id !== undefined
      ? `t${plan.template_plan_id}` : `p${plan.id}`;
    if (!groups.has(key)) groups.set(key, { key, name: plan.name, members: [] });
    groups.get(key).members.push(plan);
  });
  return [...groups.values()].map((group) => {
    const first = group.members[0];
    return {
      key: group.key,
      name: group.name,
      plan: first,
      firstHourMinor: first.first_hour_rate_minor ?? null,
      zones: first.zones || [],
      rooms: group.members.map((member) => ({
        planId: member.id,
        roomId: member.room_id,
        roomName: roomNames[String(member.room_id)] || `Room ${member.room_id}`,
      })),
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
};

export const filterPickerPlans = (pickerPlans, { query = '', roomId = 'any', zonedOnly = false } = {}) => {
  const needle = query.trim().toLowerCase();
  return pickerPlans.filter((row) => {
    if (needle && !row.name.toLowerCase().includes(needle)) return false;
    if (roomId !== 'any' && !row.rooms.some((room) => String(room.roomId) === String(roomId))) return false;
    if (zonedOnly && gatingZones(row.plan).length === 0) return false;
    return true;
  });
};

// ---- draft -> wire --------------------------------------------------------

/** Draft add -> batch items (one per chosen room plan). */
export const batchItemsForDraft = (draftAdds) => draftAdds.flatMap((add) => add.planIds.map((planId) => ({
  pricing_plan_id: planId,
  effective_from: add.effectiveFrom,
})));

export const impactBodyFor = ({ userId, draftAdds = [], endAssignments = [], today }) => ({
  user_id: userId,
  add: batchItemsForDraft(draftAdds).map(({ pricing_plan_id, effective_from }) => ({ pricing_plan_id, effective_from })),
  end: endAssignments.map((assignment) => ({
    assignment_id: assignment.id,
    effective_to: addDaysIso(today, -1),
  })),
});

/** Never-effective rows are DELETED (A3); everything else is end-dated. */
export const isNeverEffective = (assignment, today) => String(assignment.effective_from) > today;

export const countDraftChanges = (draft) => (
  draft.adds.length + draft.removals.length + Object.keys(draft.dateChanges).length
);

// removals: [{groupKey, assignmentIds, grandfather}]; dateChanges: {assignmentId: 'YYYY-MM-DD'}
export const emptyDraft = () => ({ adds: [], removals: [], dateChanges: {} });

// ---- server message --------------------------------------------------------

/** Full-sentence server `message`, verbatim (P-3) - never a bare code. */
export const serverMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (data && typeof data.message === 'string' && data.message.trim()) return data.message;
  if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') return data.error.message;
  return fallback;
};
